import { FileText, Info } from 'lucide-react';
import { useLocation } from 'wouter';
import { buildNavigationUrl } from '@/lib/breadcrumbs';
import { format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface DealRoleCardProps {
  dealRole: {
    id: string;
    dealId: string;
    dealReference: string;
    dealName: string;
    dealStatus: string;
    role: string;
    source: string;
    associatedAccountId: string | null;
    associatedAccountName: string | null;
    associationRole: string | null;
    createdAt: string;
  };
  contactId?: string;
  contactLabel?: string;
}

export default function DealRoleCard({ dealRole, contactId, contactLabel }: DealRoleCardProps) {
  const [, navigate] = useLocation();

  const isAutoPropagated = dealRole.source !== 'manual';

  const getDealStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      lead: 'bg-gray-500/10 text-gray-700 dark:text-gray-400',
      qualifying: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
      proposal: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-400',
      negotiation: 'bg-orange-500/10 text-orange-700 dark:text-orange-400',
      committed: 'bg-purple-500/10 text-purple-700 dark:text-purple-400',
      settled: 'bg-green-500/10 text-green-700 dark:text-green-400',
      lost: 'bg-red-500/10 text-red-700 dark:text-red-400',
      paid: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    };
    return statusColors[status] || statusColors.lead;
  };

  return (
    <Card className="border-l-4 border-l-orange-500">
      <CardContent className="p-3">
        <div className="flex justify-between items-start">
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm">
                {dealRole.dealName || dealRole.dealReference}
              </span>

              <Badge variant="secondary" className={getDealStatusColor(dealRole.dealStatus)}>
                {dealRole.dealStatus?.replace('_', ' ') || 'Unknown'}
              </Badge>

              {isAutoPropagated && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="secondary" className="bg-blue-500/10 text-blue-700 dark:text-blue-400 text-xs">
                        <Info className="h-3 w-3 mr-1" />
                        Auto-propagated
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Automatically propagated from {dealRole.source.replace('_', ' ')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              {dealRole.role.replace('_', ' ')} • Deal {dealRole.dealReference}
            </p>

            {dealRole.associatedAccountName && (
              <p className="text-xs text-muted-foreground">
                Associated Account: <span className="font-medium">{dealRole.associatedAccountName}</span>
                {dealRole.associationRole && ` (as ${dealRole.associationRole.replace('_', ' ')})`}
              </p>
            )}

            <p className="text-xs text-muted-foreground">
              Added: {format(new Date(dealRole.createdAt), 'dd MMM yyyy')}
            </p>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(contactId && contactLabel
              ? buildNavigationUrl(`/deals/${dealRole.dealId}`, { from: 'contact', fromId: contactId, fromLabel: contactLabel })
              : `/deals/${dealRole.dealId}`
            )}
          >
            View Deal
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
